/**
 * MenuLabel is the only always visible part of Menu.
 * Clicking it toggles visibility of MenuContent.
 */
import { Box } from '@mui/material';
import React, { memo, useState } from 'react';

const rootSx = {
  padding: 0,
  height: '100%',
  width: '100%',
  border: 0,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
};

const clickedWithMouseSx = {
  '&:focus': {
    outline: 'none',
  },
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {boolean} [props.isOpen] - injected by Menu; inert for styling (the original `.isOpen`
 *   CSS rule was empty, so open-state never changed the label's appearance).
 * @param {string} [props.isOpenClassName] - retained for API compatibility; no caller passes it.
 * @param {Function} [props.onToggleActive]
 */
const MenuLabelComponent = ({
  children,
  className = null,
  rootClassName = '',
  isOpen = false,
  isOpenClassName = null,
  onToggleActive = null,
}) => {
  const [clickedWithMouse, setClickedWithMouse] = useState(false);

  const onClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleActive();

    // Don't show focus outline if user just clicked the element with mouse
    // tab + enter creates also a click event, but its location is origin.
    const nativeEvent = e.nativeEvent;
    const isRealClick = !(nativeEvent.clientX === 0 && nativeEvent.clientY === 0);
    if (isRealClick) {
      setClickedWithMouse(true);
    }
  };

  const onBlur = () => setClickedWithMouse(false);

  const sx = {
    ...(rootClassName ? {} : rootSx),
    ...(clickedWithMouse ? clickedWithMouseSx : {}),
  };

  return (
    <Box
      className={className}
      component="button"
      onBlur={onBlur}
      onClick={onClick}
      sx={sx}
    >
      {children}
    </Box>
  );
};

const MenuLabel = memo(MenuLabelComponent);

export default MenuLabel;
